import { useEffect, useRef, useState } from 'react';

const TRIGGER_DISTANCE = 56;
const MAX_DISTANCE = 84;

export function usePullToRefresh(onRefresh: () => Promise<void>, disabled = false) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ x: number; y: number; direction: 'pending' | 'pull' | 'other' } | null>(null);
  const distanceRef = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const reset = () => {
      gesture.current = null;
      distanceRef.current = 0;
      setPullDistance(0);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (disabled || refreshing || event.touches.length !== 1 || window.scrollY > 1) return;
      const touch = event.touches[0];
      gesture.current = { x: touch.clientX, y: touch.clientY, direction: 'pending' };
    };

    const onTouchMove = (event: TouchEvent) => {
      const current = gesture.current;
      if (!current || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - current.x;
      const deltaY = touch.clientY - current.y;

      if (current.direction === 'pending' && (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6)) {
        current.direction = deltaY > 0 && deltaY > Math.abs(deltaX) * 1.2 ? 'pull' : 'other';
      }
      if (current.direction !== 'pull' || deltaY <= 0) return;

      event.preventDefault();
      const resisted = Math.min(MAX_DISTANCE, deltaY * 0.46);
      distanceRef.current = resisted;
      setPullDistance(resisted);
    };

    const onTouchEnd = () => {
      if (!gesture.current) return;
      const shouldRefresh = gesture.current.direction === 'pull' && distanceRef.current >= TRIGGER_DISTANCE;
      gesture.current = null;
      if (!shouldRefresh) {
        reset();
        return;
      }

      distanceRef.current = TRIGGER_DISTANCE;
      setPullDistance(TRIGGER_DISTANCE);
      setRefreshing(true);
      void onRefresh().finally(() => {
        setRefreshing(false);
        reset();
      });
    };

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: true });
    element.addEventListener('touchcancel', reset, { passive: true });
    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', reset);
    };
  }, [disabled, onRefresh, refreshing]);

  return { containerRef, pullDistance, refreshing, ready: pullDistance >= TRIGGER_DISTANCE };
}
